import { DropDownItem } from "../components/Input/MultiSelectorDropdown/MultiSelectorDropDown";

export interface UcumCategory {
    name: string;
    items: DropDownItem[];
}

// Categorized UCUM (Unified Code for Units of Measure) units
// Based on official UCUM specification and common usage patterns
// Ordered by relevance for observability of distributed systems
export const UCUM_CATEGORIES: UcumCategory[] = [
    {
        name: "Dimensionless",
        items: [
            { displayName: "1 (dimensionless)", value: "1" },
            { displayName: "% (percent)", value: "%" },
        ]
    },
    {
        name: "Application Metrics",
        items: [
            { displayName: "requests/s (requests per second)", value: "{requests}/s" },
            { displayName: "errors/s (errors per second)", value: "{errors}/s" },
            { displayName: "ops/s (operations per second)", value: "{ops}/s" },
            { displayName: "transactions/s (transactions per second)", value: "{transactions}/s" },
            { displayName: "packets/s (packets per second)", value: "{packets}/s" },
            { displayName: "messages/s (messages per second)", value: "{messages}/s" },
            { displayName: "events/s (events per second)", value: "{events}/s" },
        ]
    },
    {
        name: "Information (Binary)",
        items: [
            { displayName: "B (byte)", value: "By" },
            { displayName: "KiB (kibibyte)", value: "KiBy" },
            { displayName: "MiB (mebibyte)", value: "MiBy" },
            { displayName: "GiB (gibibyte)", value: "GiBy" },
            { displayName: "TiB (tebibyte)", value: "TiBy" },
            { displayName: "PiB (pebibyte)", value: "PiBy" },
        ]
    },
    {
        name: "Information (Decimal)",
        items: [
            { displayName: "bit", value: "bit" },
            { displayName: "B (byte)", value: "By" },
            { displayName: "kB (kilobyte)", value: "kBy" },
            { displayName: "MB (megabyte)", value: "MBy" },
            { displayName: "GB (gigabyte)", value: "GBy" },
            { displayName: "TB (terabyte)", value: "TBy" },
            { displayName: "PB (petabyte)", value: "PBy" },
        ]
    },
    {
        name: "Data Rate (Bytes - Binary)",
        items: [
            { displayName: "B/s (bytes per second)", value: "By/s" },
            { displayName: "KiB/s (kibibytes per second)", value: "KiBy/s" },
            { displayName: "MiB/s (mebibytes per second)", value: "MiBy/s" },
            { displayName: "GiB/s (gibibytes per second)", value: "GiBy/s" },
            { displayName: "TiB/s (tebibytes per second)", value: "TiBy/s" },
        ]
    },
    {
        name: "Data Rate (Bytes - Decimal)",
        items: [
            { displayName: "B/s (bytes per second)", value: "By/s" },
            { displayName: "kB/s (kilobytes per second)", value: "kBy/s" },
            { displayName: "MB/s (megabytes per second)", value: "MBy/s" },
            { displayName: "GB/s (gigabytes per second)", value: "GBy/s" },
        ]
    },
    {
        name: "Data Rate (Bits)",
        items: [
            { displayName: "bit/s (bits per second)", value: "bit/s" },
            { displayName: "kbit/s (kilobits per second)", value: "kbit/s" },
            { displayName: "Mbit/s (megabits per second)", value: "Mbit/s" },
            { displayName: "Gbit/s (gigabits per second)", value: "Gbit/s" },
        ]
    },
    {
        name: "System Resources",
        items: [
            { displayName: "connections (active connections)", value: "{connections}" },
            { displayName: "threads (active threads)", value: "{threads}" },
            { displayName: "processes (active processes)", value: "{processes}" },
            { displayName: "users (active users)", value: "{users}" },
            { displayName: "sessions (active sessions)", value: "{sessions}" },
            { displayName: "CPU cores", value: "{CPU}" },
            { displayName: "CPU usage %", value: "%{CPU}" },
            { displayName: "memory usage %", value: "%{memory}" },
            { displayName: "disk usage %", value: "%{disk}" },
            { displayName: "network usage %", value: "%{network}" },
        ]
    },
    {
        name: "Time",
        items: [
            { displayName: "ns (nanosecond)", value: "ns" },
            { displayName: "μs (microsecond)", value: "us" },
            { displayName: "ms (millisecond)", value: "ms" },
            { displayName: "s (second)", value: "s" },
            { displayName: "min (minute)", value: "min" },
            { displayName: "h (hour)", value: "h" },
            { displayName: "d (day)", value: "d" },
            { displayName: "wk (week)", value: "wk" },
            { displayName: "mo (month)", value: "mo" },
            { displayName: "a (year)", value: "a" },
        ]
    },
    {
        name: "Frequency",
        items: [
            { displayName: "Hz (hertz)", value: "Hz" },
            { displayName: "kHz (kilohertz)", value: "kHz" },
            { displayName: "MHz (megahertz)", value: "MHz" },
            { displayName: "GHz (gigahertz)", value: "GHz" },
            { displayName: "/s (per second)", value: "/s" },
            { displayName: "/min (per minute)", value: "/min" },
            { displayName: "/h (per hour)", value: "/h" },
            { displayName: "/d (per day)", value: "/d" },
        ]
    },
    {
        name: "Timestamp",
        items: [
            { displayName: "Unix timestamp (seconds)", value: "unix_s" },
            { displayName: "Unix timestamp (milliseconds)", value: "unix_ms" },
            { displayName: "Unix timestamp (microseconds)", value: "unix_us" },
            { displayName: "Unix timestamp (nanoseconds)", value: "unix_ns" },
        ]
    },
    {
        name: "Temperature",
        items: [
            { displayName: "K (kelvin)", value: "K" },
            { displayName: "°C (Celsius)", value: "Cel" },
            { displayName: "°F (Fahrenheit)", value: "[degF]" },
        ]
    },
    {
        name: "Power",
        items: [
            { displayName: "W (watt)", value: "W" },
            { displayName: "kW (kilowatt)", value: "kW" },
            { displayName: "MW (megawatt)", value: "MW" },
            { displayName: "GW (gigawatt)", value: "GW" },
            { displayName: "hp (horsepower)", value: "[hp]" },
        ]
    },
    {
        name: "Electric Voltage",
        items: [
            { displayName: "V (volt)", value: "V" },
            { displayName: "mV (millivolt)", value: "mV" },
            { displayName: "kV (kilovolt)", value: "kV" },
        ]
    },
    {
        name: "Electric Current",
        items: [
            { displayName: "A (ampere)", value: "A" },
            { displayName: "mA (milliampere)", value: "mA" },
            { displayName: "Ah (ampere hour)", value: "A.h" },
            { displayName: "mAh (milliampere hour)", value: "mA.h" },
        ]
    },
    {
        name: "Length",
        items: [
            { displayName: "nm (nanometer)", value: "nm" },
            { displayName: "μm (micrometer)", value: "um" },
            { displayName: "mm (millimeter)", value: "mm" },
            { displayName: "cm (centimeter)", value: "cm" },
            { displayName: "dm (decimeter)", value: "dm" },
            { displayName: "m (meter)", value: "m" },
            { displayName: "km (kilometer)", value: "km" },
            { displayName: "in (inch)", value: "[in_i]" },
            { displayName: "ft (foot)", value: "[ft_i]" },
            { displayName: "yd (yard)", value: "[yd_i]" },
            { displayName: "mi (mile)", value: "[mi_i]" },
        ]
    },
    {
        name: "Mass",
        items: [
            { displayName: "ng (nanogram)", value: "ng" },
            { displayName: "μg (microgram)", value: "ug" },
            { displayName: "mg (milligram)", value: "mg" },
            { displayName: "g (gram)", value: "g" },
            { displayName: "kg (kilogram)", value: "kg" },
            { displayName: "t (metric ton)", value: "t" },
            { displayName: "oz (ounce)", value: "[oz_av]" },
            { displayName: "lb (pound)", value: "[lb_av]" },
        ]
    },
    {
        name: "Area",
        items: [
            { displayName: "mm² (square millimeter)", value: "mm2" },
            { displayName: "cm² (square centimeter)", value: "cm2" },
            { displayName: "m² (square meter)", value: "m2" },
            { displayName: "km² (square kilometer)", value: "km2" },
            { displayName: "ha (hectare)", value: "har" },
            { displayName: "in² (square inch)", value: "[sin_i]" },
            { displayName: "ft² (square foot)", value: "[sft_i]" },
        ]
    },
    {
        name: "Volume",
        items: [
            { displayName: "ml (milliliter)", value: "ml" },
            { displayName: "l (liter)", value: "l" },
            { displayName: "m³ (cubic meter)", value: "m3" },
            { displayName: "gal (US gallon)", value: "[gal_us]" },
            { displayName: "qt (US quart)", value: "[qt_us]" },
            { displayName: "pt (US pint)", value: "[pt_us]" },
            { displayName: "fl oz (US fluid ounce)", value: "[foz_us]" },
        ]
    },
    {
        name: "Speed",
        items: [
            { displayName: "m/s (meters per second)", value: "m/s" },
            { displayName: "km/h (kilometers per hour)", value: "km/h" },
            { displayName: "mph (miles per hour)", value: "[mi_i]/h" },
            { displayName: "kn (knot)", value: "[kn_i]" },
        ]
    },
    {
        name: "Force",
        items: [
            { displayName: "N (newton)", value: "N" },
            { displayName: "kN (kilonewton)", value: "kN" },
            { displayName: "lbf (pound force)", value: "[lbf_av]" },
        ]
    },
    {
        name: "Pressure",
        items: [
            { displayName: "Pa (pascal)", value: "Pa" },
            { displayName: "kPa (kilopascal)", value: "kPa" },
            { displayName: "MPa (megapascal)", value: "MPa" },
            { displayName: "bar (bar)", value: "bar" },
            { displayName: "mbar (millibar)", value: "mbar" },
            { displayName: "atm (atmosphere)", value: "atm" },
            { displayName: "mmHg (millimeter of mercury)", value: "mm[Hg]" },
            { displayName: "psi (pounds per square inch)", value: "[psi]" },
        ]
    },
    {
        name: "Energy",
        items: [
            { displayName: "J (joule)", value: "J" },
            { displayName: "kJ (kilojoule)", value: "kJ" },
            { displayName: "MJ (megajoule)", value: "MJ" },
            { displayName: "cal (calorie)", value: "cal" },
            { displayName: "kcal (kilocalorie)", value: "kcal" },
            { displayName: "Wh (watt hour)", value: "W.h" },
            { displayName: "kWh (kilowatt hour)", value: "kW.h" },
            { displayName: "MWh (megawatt hour)", value: "MW.h" },
            { displayName: "BTU (British thermal unit)", value: "[Btu_IT]" },
        ]
    },
    {
        name: "Electric Resistance",
        items: [
            { displayName: "Ω (ohm)", value: "Ohm" },
            { displayName: "kΩ (kiloohm)", value: "kOhm" },
            { displayName: "MΩ (megaohm)", value: "MOhm" },
        ]
    },
    {
        name: "Electric Capacitance",
        items: [
            { displayName: "F (farad)", value: "F" },
            { displayName: "μF (microfarad)", value: "uF" },
            { displayName: "nF (nanofarad)", value: "nF" },
            { displayName: "pF (picofarad)", value: "pF" },
        ]
    },
    {
        name: "Electric Inductance",
        items: [
            { displayName: "H (henry)", value: "H" },
            { displayName: "mH (millihenry)", value: "mH" },
            { displayName: "μH (microhenry)", value: "uH" },
        ]
    },
    {
        name: "Electric Charge",
        items: [
            { displayName: "C (coulomb)", value: "C" },
        ]
    },
    {
        name: "Concentration",
        items: [
            { displayName: "mol/l (molar)", value: "mol/l" },
            { displayName: "mmol/l (millimolar)", value: "mmol/l" },
            { displayName: "μmol/l (micromolar)", value: "umol/l" },
            { displayName: "nmol/l (nanomolar)", value: "nmol/l" },
            { displayName: "g/l (grams per liter)", value: "g/l" },
            { displayName: "mg/l (milligrams per liter)", value: "mg/l" },
            { displayName: "μg/l (micrograms per liter)", value: "ug/l" },
            { displayName: "ng/l (nanograms per liter)", value: "ng/l" },
        ]
    },
    {
        name: "Currency",
        items: [
            { displayName: "USD (US Dollar)", value: "{USD}" },
            { displayName: "EUR (Euro)", value: "{EUR}" },
            { displayName: "GBP (British Pound)", value: "{GBP}" },
            { displayName: "JPY (Japanese Yen)", value: "{JPY}" },
            { displayName: "CNY (Chinese Yuan)", value: "{CNY}" },
            { displayName: "CHF (Swiss Franc)", value: "{CHF}" },
            { displayName: "CAD (Canadian Dollar)", value: "{CAD}" },
            { displayName: "AUD (Australian Dollar)", value: "{AUD}" },
            { displayName: "NZD (New Zealand Dollar)", value: "{NZD}" },
            { displayName: "INR (Indian Rupee)", value: "{INR}" },
            { displayName: "KRW (South Korean Won)", value: "{KRW}" },
            { displayName: "HKD (Hong Kong Dollar)", value: "{HKD}" },
            { displayName: "SGD (Singapore Dollar)", value: "{SGD}" },
            { displayName: "SEK (Swedish Krona)", value: "{SEK}" },
            { displayName: "NOK (Norwegian Krone)", value: "{NOK}" },
            { displayName: "DKK (Danish Krone)", value: "{DKK}" },
            { displayName: "MXN (Mexican Peso)", value: "{MXN}" },
            { displayName: "BRL (Brazilian Real)", value: "{BRL}" },
            { displayName: "RUB (Russian Ruble)", value: "{RUB}" },
            { displayName: "ZAR (South African Rand)", value: "{ZAR}" },
            { displayName: "AED (UAE Dirham)", value: "{AED}" },
            { displayName: "SAR (Saudi Riyal)", value: "{SAR}" },
            { displayName: "TRY (Turkish Lira)", value: "{TRY}" },
            { displayName: "PLN (Polish Zloty)", value: "{PLN}" },
            { displayName: "THB (Thai Baht)", value: "{THB}" },
            { displayName: "MYR (Malaysian Ringgit)", value: "{MYR}" },
            { displayName: "IDR (Indonesian Rupiah)", value: "{IDR}" },
            { displayName: "PHP (Philippine Peso)", value: "{PHP}" },
            { displayName: "CZK (Czech Koruna)", value: "{CZK}" },
            { displayName: "HUF (Hungarian Forint)", value: "{HUF}" },
            { displayName: "ILS (Israeli Shekel)", value: "{ILS}" },
            { displayName: "CLP (Chilean Peso)", value: "{CLP}" },
            { displayName: "ARS (Argentine Peso)", value: "{ARS}" },
            { displayName: "COP (Colombian Peso)", value: "{COP}" },
            { displayName: "USD/s (USD per second)", value: "{USD}/s" },
            { displayName: "USD/min (USD per minute)", value: "{USD}/min" },
            { displayName: "USD/h (USD per hour)", value: "{USD}/h" },
            { displayName: "USD/d (USD per day)", value: "{USD}/d" },
            { displayName: "USD/mo (USD per month)", value: "{USD}/mo" },
        ]
    }
];

// Flattened list for backward compatibility and search functionality
export const UCUM_UNITS: DropDownItem[] = UCUM_CATEGORIES.flatMap(category => category.items);